/**
 * AuthContext Adapter
 * 기존 AuthContext를 DDD 구조와 연결하는 어댑터
 * 점진적 마이그레이션을 위한 호환성 레이어
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { User, StudySession } from '../types';
import { useUserStore } from '../store/userStore';
import {
  LegacyAuthAdapter,
  legacyAuthAdapter,
  LegacyLoginCredentials,
  LegacyLoginResponse
} from '../domains/auth/adapters/LegacyAuthAdapter';
import { AuthToken } from '../domains/auth/value-objects/AuthToken';
import { AuthLocalStorageAdapter } from '../domains/auth/repositories/AuthLocalStorageAdapter';
import { startTokenRefresh, stopTokenRefresh } from '../lib/supabaseClient';

// 기존 AuthContext 인터페이스 그대로 유지
export interface AuthContextType {
  // 인증 상태
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // 토큰 상태
  accessToken: string | null;
  refreshToken: string | null;

  // 인증 액션
  login: (credentials: LoginCredentials) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<string>;

  // 세션 상태
  currentSession: StudySession | null;

  // 유틸리티
  checkAuthStatus: () => Promise<boolean>;
  clearAuth: () => void;
}

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  access_token: string;
  refresh_token: string;
  token_type: string;
  session_id?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider with DDD Adapter
 * 기존 AuthProvider 인터페이스를 유지하면서 내부적으로는 DDD 구조 사용
 */
export const AuthProviderAdapter: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    currentUser,
    currentSession,
    setCurrentUser,
    setCurrentSession,
    setLoading,
    isLoading,
  } = useUserStore();

  // 토큰 상태 관리 (기존과 동일)
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [adapter] = useState<LegacyAuthAdapter>(legacyAuthAdapter);

  // 토큰 저장소 관리 (기존과 동일)
  const saveTokens = async (access: string, refresh: string) => {
    // 디버깅: 토큰 저장 확인

    if (access === refresh) {
      console.error('🚨 경고: access_token과 refresh_token이 동일합니다! 이는 오류입니다!');
    }

    // Legacy 키에 저장
    setAccessToken(access);
    setRefreshToken(refresh);
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);

    // [2026-01-05] Supabase 클라이언트에 세션 설정 (Supabase 사용 시에만)
    try {
      const { supabase } = await import('../lib/supabaseClient');
      if (supabase) {
        await supabase.auth.setSession({
          access_token: access,
          refresh_token: refresh,
        });
        console.log('✅ [Supabase] 세션 설정 완료');
      }
    } catch (supabaseError) {
      // Supabase 없으면 무시
    }

    // DDD 키에도 저장 (TokenService 호환성)
    try {
      const authToken = AuthToken.fromJWT(access, refresh);
      AuthLocalStorageAdapter.saveToken(authToken);
      AuthLocalStorageAdapter.updateLastActivity();
    } catch (tokenError) {
      console.warn('⚠️ DDD 토큰 저장 실패 (계속 진행):', tokenError);
    }
  };

  const clearTokens = async () => {
    setAccessToken(null);
    setRefreshToken(null);
    // Legacy 키 삭제
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('user');
    // 캐시된 조직 정보 삭제 (다른 계정 로그인 시 이전 정보 표시 방지)
    localStorage.removeItem('cached_organization_info');
    // DDD 키도 삭제
    AuthLocalStorageAdapter.clearAll();

    // [2026-01-05] Supabase 로그아웃 (Supabase 사용 시에만)
    try {
      const { supabase } = await import('../lib/supabaseClient');
      if (supabase) {
        await supabase.auth.signOut();
      }
    } catch (supabaseError) {
      // Supabase 없으면 무시
    }
  };

  // 로그인 함수 - 어댑터 사용
  const login = async (
    credentials: LoginCredentials
  ): Promise<LoginResponse> => {
    try {
      setLoading(true);

      // Legacy 형태로 어댑터에 전달
      const legacyCredentials: LegacyLoginCredentials = {
        username: credentials.username,
        password: credentials.password,
      };

      const response = await adapter.login(legacyCredentials);

      // 토큰 저장 (Supabase 세션도 설정)
      await saveTokens(response.access_token, response.refresh_token);

      // 사용자 정보 저장 - Legacy User를 현재 User 타입으로 변환
      const user: User = {
        id: response.user.id,
        name: response.user.name,
        user_number: response.user.user_number,
        phone: response.user.phone,
        email: response.user.email,
        is_active: response.user.is_active,
        created_at: response.user.created_at,
        updated_at: response.user.updated_at,
        role: response.user.role,
        org_id: response.user.org_id,
        permissions: response.user.permissions,
        first_login: response.user.first_login,
        temp_password: response.user.temp_password,
        member_number: response.user.member_number,
        studyroom_name: response.user.studyroom_name,
        business_id: response.user.business_id,
      };

      setCurrentUser(user);

      // 사용자 정보를 localStorage에 저장 (새로고침 시 빠른 복원을 위해)
      localStorage.setItem('user', JSON.stringify(user));

      // 조직 정보 캐싱 (organization_name이 있을 경우)
      if (response.user.organization_name && response.user.organization_id) {
        const orgInfo = {
          id: response.user.organization_id,
          name: response.user.organization_name,
          description: 'NewAttend 시스템으로 운영되는 스마트 독서실입니다.',
          settings: {},
        };
        localStorage.setItem('cached_organization_info', JSON.stringify(orgInfo));
        // 사이드바 조직명 즉시 업데이트를 위한 이벤트 발생
        window.dispatchEvent(new Event('organizationNameUpdated'));
      }

      // 세션 ID 저장
      if (response.session_id) {
        localStorage.setItem('sessionId', response.session_id);
      }

      // [2026-01-06] 토큰 자동 갱신 모니터링 시작
      startTokenRefresh();

      // 기존 인터페이스에 맞는 응답 반환
      return {
        user,
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        token_type: response.token_type,
        session_id: response.session_id,
      };
    } catch (error) {
      console.error('❌ [Adapter] 로그인 실패:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 로그아웃 함수 - 어댑터 사용
  const logout = async (): Promise<void> => {
    try {
      setLoading(true);

      // [2026-01-06] 토큰 자동 갱신 모니터링 중지
      stopTokenRefresh();

      // 어댑터를 통한 로그아웃
      await adapter.logout();

      // 로컬 상태 정리
      clearAuth();

    } catch (error) {
      console.error('❌ [Adapter] 로그아웃 실패:', error);
      // 로그아웃 실패해도 로컬 상태는 정리
      clearAuth();
    } finally {
      setLoading(false);
    }
  };

  // 토큰 갱신 함수 - 어댑터 사용
  const refreshAccessToken = async (): Promise<string> => {
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const newAccessToken = await adapter.refreshAccessToken();

      // 새 액세스 토큰 저장
      setAccessToken(newAccessToken);
      localStorage.setItem('accessToken', newAccessToken);


      return newAccessToken;
    } catch (error) {
      console.error('❌ [Adapter] 토큰 갱신 실패:', error);

      // 토큰 갱신 실패해도 즉시 로그아웃하지 않음
      // 사용자가 다시 로그인할 때까지 대기
      console.warn('⚠️ [Adapter] 토큰 갱신 실패 - 다음 로그인 시 재인증 필요');
      throw error;
    }
  };

  // 인증 상태 확인 함수 - 어댑터 사용
  const checkAuthStatus = async (): Promise<boolean> => {
    try {
      // 먼저 로컬 토큰이 있는지 확인
      const storedAccessToken = localStorage.getItem('accessToken');
      const storedRefreshToken = localStorage.getItem('refreshToken');
      // 두 가지 키 모두 확인 (호환성 위해)
      const storedUser = localStorage.getItem('user') || localStorage.getItem('auth_user_data');
      
      console.log('🔍 [Adapter] 인증 상태 확인 중...', {
        hasAccessToken: !!storedAccessToken,
        hasRefreshToken: !!storedRefreshToken,
        hasUser: !!storedUser
      });

      // 토큰이 없으면 바로 false 반환 (서버 호출 안함)
      if (!storedAccessToken) {
        return false;
      }

      // 저장된 사용자 정보가 있으면 먼저 복원 (서버 호출 전)
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          const user: User = {
            id: userData.id,
            name: userData.name,
            user_number: userData.user_number,
            phone: userData.phone || '',
            email: userData.email,
            is_active: userData.is_active !== undefined ? userData.is_active : true,
            created_at: userData.created_at || new Date().toISOString(),
            updated_at: userData.updated_at || new Date().toISOString(),
            role: userData.role,
            org_id: userData.org_id,
            permissions: userData.permissions,
            first_login: userData.first_login,
            temp_password: userData.temp_password,
            member_number: userData.member_number,
            studyroom_name: userData.studyroom_name,
            business_id: userData.business_id,
          };

          setCurrentUser(user);
          setAccessToken(storedAccessToken);
          if (storedRefreshToken) {
            setRefreshToken(storedRefreshToken);
          }

          // DDD 키에 토큰 동기화 (TokenService 호환성)
          try {
            const authToken = AuthToken.fromJWT(storedAccessToken, storedRefreshToken || '');
            AuthLocalStorageAdapter.saveToken(authToken);
            AuthLocalStorageAdapter.updateLastActivity();
          } catch (tokenSyncError) {
            // JWT 파싱 실패 시 무시 (기존 토큰으로 동작)
          }

          return true;
        } catch (parseError) {
          console.error('❌ [Adapter] 저장된 사용자 정보 파싱 실패:', parseError);
        }
      }

      // 저장된 사용자 정보가 없거나 파싱 실패시에만 서버 호출
      
      try {
        // 실제 백엔드 API로 토큰 검증 및 사용자 정보 조회
        const response = await fetch('/api/v1/supabase-auth/me', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${storedAccessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const userData = await response.json();
          
          // 서버에서 받은 실제 사용자 정보로 User 객체 생성
          const user: User = {
            id: userData.user_id || userData.id || 'unknown',
            name: userData.name || userData.username || 'Unknown User',
            user_number: userData.user_number || userData.email || 'unknown',
            phone: userData.phone || '',
            email: userData.email || 'unknown@example.com',
            is_active: userData.is_active !== false,
            created_at: userData.created_at || new Date().toISOString(),
            updated_at: userData.updated_at || new Date().toISOString(),
            role: userData.role || 'user',
            org_id: userData.org_id,
            permissions: userData.permissions || [],
            first_login: userData.first_login,
            temp_password: userData.temp_password,
            member_number: userData.member_number,
            studyroom_name: userData.studyroom_name,
            business_id: userData.business_id,
          };

          setCurrentUser(user);
          setAccessToken(storedAccessToken);
          if (storedRefreshToken) {
            setRefreshToken(storedRefreshToken);
          }

          // localStorage에 사용자 정보 저장 (두 키에 모두 저장하여 호환성 보장)
          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('auth_user_data', JSON.stringify(user));

          return true;
          
        } else if (response.status === 401) {
          console.warn('⚠️ [Adapter] 토큰 무효 - 저장된 토큰 유지, 다음 로그인 시 갱신');
          // 401 에러가 나도 즉시 로그아웃하지 않음
          // 사용자가 명시적으로 로그인할 때까지 대기
          return false;
        } else {
          console.error(`❌ [Adapter] 서버 응답 오류: ${response.status}`);
          // 네트워크 오류 등은 토큰을 보존
          return false;
        }
      } catch (serverError) {
        console.error('❌ [Adapter] 서버 연결 실패:', serverError);
        // 네트워크 오류 시에도 토큰 보존
        return false;
      }
    } catch (error) {
      console.warn('❌ [Adapter] 인증 상태 확인 전체 실패:', error);
      
      // 전체 실패시에도 토큰이 있으면 보존
      const storedAccessToken = localStorage.getItem('accessToken');
      if (storedAccessToken) {
        setAccessToken(storedAccessToken);
        const storedRefreshToken = localStorage.getItem('refreshToken');
        if (storedRefreshToken) {
          setRefreshToken(storedRefreshToken);
        }
      }
      
      return false;
    }
  };

  // 인증 상태 완전 정리 함수 (기존과 동일)
  const clearAuth = () => {
    clearTokens();
    setCurrentUser(null);
    setCurrentSession(null);
  };

  // 앱 초기화시 인증 상태 복원 (기존과 동일하지만 어댑터 사용)
  useEffect(() => {
    const initializeAuth = async () => {

      try {
        const isValid = await checkAuthStatus();
        if (!isValid) {
        }
      } catch (error) {
        console.error('[Adapter] 초기화 실패:', error);
        clearAuth();
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  // 사용자 활동 추적 및 자동 로그아웃 (10분 비활성) - 비활성화됨
  // useEffect(() => {
  //   if (!accessToken || !currentUser) return;

  //   let lastActivityTime = Date.now();
  //   let inactivityTimer: NodeJS.Timeout;

  //   const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10분
  //   const WARNING_TIME = 1 * 60 * 1000; // 로그아웃 1분 전 경고

  //   // 사용자 활동 감지 이벤트들
  //   const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

  //   const resetActivityTimer = () => {
  //     lastActivityTime = Date.now();

  //     // 기존 타이머 제거
  //     if (inactivityTimer) {
  //       clearTimeout(inactivityTimer);
  //     }

  //     // 새 타이머 설정 (10분 후 로그아웃)
  //     inactivityTimer = setTimeout(() => {
  //       console.log('🕐 [Auto-Logout] 10분간 비활성으로 자동 로그아웃');
  //       clearAuth();
  //       alert('10분간 활동이 없어 자동으로 로그아웃되었습니다.\n보안을 위한 조치입니다.');

  //       // 로그인 페이지로 이동 (서브도메인에서는 /login으로)
  //       if (!window.location.pathname.includes('/tablet/gate-operation')) {
  //         const hostname = window.location.hostname;
  //         if (hostname === 'studyroom.newattend.com' || hostname.startsWith('studyroom.')) {
  //           window.location.href = '/login';
  //         } else {
  //           window.location.href = 'https://studyroom.newattend.com/login';
  //         }
  //       }
  //     }, INACTIVITY_TIMEOUT);

  //     // 1분 전 경고 타이머도 설정
  //     setTimeout(() => {
  //       const timeSinceLastActivity = Date.now() - lastActivityTime;
  //       if (timeSinceLastActivity >= (INACTIVITY_TIMEOUT - WARNING_TIME)) {
  //         console.warn('⚠️ [Auto-Logout] 1분 후 자동 로그아웃 예정');

  //         const shouldStay = window.confirm('1분 후 자동 로그아웃됩니다.\n계속 사용하시겠습니까?');
  //         if (shouldStay) {
  //           resetActivityTimer(); // 사용자가 계속 사용을 원하면 타이머 리셋
  //         }
  //       }
  //     }, INACTIVITY_TIMEOUT - WARNING_TIME);
  //   };

  //   // 활동 이벤트 리스너 등록
  //   const handleActivity = () => {
  //     resetActivityTimer();
  //   };

  //   activityEvents.forEach(event => {
  //     document.addEventListener(event, handleActivity, true);
  //   });

  //   // 초기 타이머 설정
  //   resetActivityTimer();

  //   console.log('🕐 [Auto-Logout] 10분 비활성 자동 로그아웃 활성화');

  //   return () => {
  //     // 클린업
  //     if (inactivityTimer) {
  //       clearTimeout(inactivityTimer);
  //     }
  //     activityEvents.forEach(event => {
  //       document.removeEventListener(event, handleActivity, true);
  //     });
  //   };
  // }, [accessToken, currentUser]);

  // [2025-12-19] 중복 토큰 체크 제거
  // API 인터셉터에서 401 에러 시 자동으로 토큰 갱신하므로
  // 주기적인 토큰 체크는 불필요함 (중복 요청으로 인한 에러 발생 원인)
  // 토큰 갱신은 authApi.ts의 인터셉터에서만 담당

  const contextValue: AuthContextType = {
    // 상태 (기존과 동일)
    user: currentUser,
    isAuthenticated: !!currentUser && !!accessToken,
    isLoading: isLoading || !isInitialized,
    accessToken,
    refreshToken,
    currentSession,

    // 액션 (어댑터 사용)
    login,
    logout,
    refreshAccessToken,
    checkAuthStatus,
    clearAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;